/**
 * Timestamp formatting utility
 * Converts UTC ISO-8601 timestamps to user's local timezone
 * Default timezone: America/Aruba (UTC-4, no DST)
 */

const DEFAULT_TIMEZONE = 'America/Aruba';

/**
 * Get the user's timezone, defaulting to America/Aruba if unavailable
 */
export const getUserTimezone = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

/**
 * Parse a UTC timestamp string to a Date object
 * Handles various ISO-8601 formats
 */
export const parseUTCTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  try {
    // Handle various formats
    let normalized = timestamp;
    
    // Ensure it's treated as UTC if no timezone specified
    if (!normalized.endsWith('Z') && !normalized.includes('+') && !normalized.includes('-', 10)) {
      normalized = normalized + 'Z';
    }
    
    const date = new Date(normalized);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

/**
 * Format a UTC timestamp for display in the user's local timezone
 * 
 * Display format:
 * - Same day: HH:mm (e.g., "14:30")
 * - Yesterday: "Yesterday HH:mm" (e.g., "Yesterday 14:30")
 * - Older: DD-MM-YYYY HH:mm (e.g., "26-02-2026 14:30")
 * 
 * @param {string} utcTimestamp - ISO-8601 UTC timestamp
 * @param {string} timezone - Optional timezone override
 * @returns {string} Formatted timestamp string
 */
export const formatTimestamp = (utcTimestamp, timezone = null) => {
  const date = parseUTCTimestamp(utcTimestamp);
  if (!date) return utcTimestamp || '';
  
  const tz = timezone || getUserTimezone();
  const now = new Date();
  
  // Get date components in user's timezone
  const options = { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false };
  const dateOptions = { timeZone: tz, day: '2-digit', month: '2-digit', year: 'numeric' };
  
  try {
    const timeStr = date.toLocaleTimeString('en-GB', options);
    const dateStr = date.toLocaleDateString('en-GB', dateOptions).replace(/\//g, '-');
    
    // Get today and yesterday in user's timezone
    const todayStr = now.toLocaleDateString('en-GB', { timeZone: tz }).replace(/\//g, '-');
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toLocaleDateString('en-GB', { timeZone: tz }).replace(/\//g, '-');
    const msgDateStr = date.toLocaleDateString('en-GB', { timeZone: tz }).replace(/\//g, '-');
    
    if (msgDateStr === todayStr) {
      return timeStr;
    } else if (msgDateStr === yesterdayStr) {
      return `Yesterday ${timeStr}`;
    } else {
      return `${dateStr} ${timeStr}`;
    }
  } catch {
    // Fallback for browsers with limited Intl support
    return date.toISOString().slice(0, 16).replace('T', ' ');
  }
};

/**
 * Format timestamp for conversation list (shorter format)
 * - Same day: HH:mm
 * - This week: Day name (e.g., "Mon")
 * - Older: DD/MM
 */
export const formatListTimestamp = (utcTimestamp, timezone = null) => {
  const date = parseUTCTimestamp(utcTimestamp);
  if (!date) return '';
  
  const tz = timezone || getUserTimezone();
  const now = new Date();
  
  try {
    const timeOptions = { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false };
    const timeStr = date.toLocaleTimeString('en-GB', timeOptions);
    
    // Get today in user's timezone
    const todayStr = now.toLocaleDateString('en-GB', { timeZone: tz });
    const msgDateStr = date.toLocaleDateString('en-GB', { timeZone: tz });
    
    if (msgDateStr === todayStr) {
      return timeStr;
    }
    
    // Check if within last 7 days
    const daysDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff < 7) {
      const dayName = date.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' });
      return dayName;
    }
    
    // Older - show date
    const shortDate = date.toLocaleDateString('en-GB', { 
      timeZone: tz, 
      day: '2-digit', 
      month: 'short' 
    });
    return shortDate;
  } catch {
    return '';
  }
};

/**
 * Get raw Date object from UTC timestamp for sorting purposes
 */
export const getTimestampForSort = (utcTimestamp) => {
  const date = parseUTCTimestamp(utcTimestamp);
  return date ? date.getTime() : 0;
};

export default formatTimestamp;
